import { isCurrentEnrollment } from '@/lib/enrollment-kind';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
} from 'firebase/firestore';
import { auth, db, functions } from '@/lib/firebase';
import { certificationApi } from '@/lib/certification-api';
import { DRXError } from '@/lib/drx-error';
import type {
  IEnrollmentRepository,
  CreateEnrollmentData,
  GetEnrollmentRecord,
  EnrollmentWithCourse,
  CourseQuizRef,
  CourseCertificateRef,
  EligibleStudent,
} from './interfaces/enrollment.repository.interface';
import { profileDocRef } from "@/lib/public-profile";
import { httpsCallable } from 'firebase/functions';

/**
 * Resolves a course ID from potentially nested references or path strings.
 */
function resolveCourseDocId(value: unknown): string | null {
  if (value === null || value === undefined) return null;

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (typeof record.id === 'string' && record.id.trim()) return record.id.trim();
    if (typeof record.path === 'string' && record.path.trim()) {
      const parts = record.path.split('/').filter(Boolean);
      return parts.length > 0 ? parts[parts.length - 1] : null;
    }
  }

  const raw = String(value).trim();
  const lower = raw.toLowerCase();
  if (!raw || lower === 'null' || lower === 'undefined') return null;

  if (raw.includes('/')) {
    const segments = raw.split('/').filter(Boolean);
    return segments.length > 0 ? segments[segments.length - 1] : null;
  }

  return raw;
}

function enrolledAtToIso(value: unknown): string {
  if (value && typeof value === 'object' && 'toDate' in value) {
    const fn = (value as { toDate?: () => Date }).toDate;
    if (typeof fn === 'function') {
      try {
        return fn.call(value).toISOString();
      } catch {
        /* ignore */
      }
    }
  }
  if (typeof value === 'string' && value) return value;
  return new Date().toISOString();
}

// Enrollment status is written in both cases: purchases create 'active',
// course completion (Cloud Functions) sets 'COMPLETED'. Filtering on one
// casing hid finished courses from a learner's dashboard -- which fell back
// to the purchase record and showed them as "In Progress, 0%" with no
// certificate link -- and hid every in-progress learner from an
// instructor's student list.
const LIVE_ENROLLMENT_STATUSES = ['active', 'ACTIVE', 'pending', 'PENDING', 'completed', 'COMPLETED'];

export class EnrollmentRepository implements IEnrollmentRepository {
  private enrollmentsCol = 'enrollments';
  private coursePurchasesCol = 'coursePurchases';
  private resourcePurchasesCol = 'resourcePurchases';

  /**
   * Enroll in a course. Enrollments are server-written (firestore.rules): a
   * student can only enroll themselves in a FREE course this way; paid courses
   * go through Stripe checkout, and staff enroll others via createEnrollment.
   */
  async enrollInCourse(userId: string, courseId: string): Promise<GetEnrollmentRecord> {
    try {
      if (auth.currentUser?.uid && auth.currentUser.uid !== userId) {
        return await this.createEnrollment({ userId, courseId });
      }
      const { enrollmentId } = await certificationApi.enrollInFreeCourse(courseId);
      return {
        id: enrollmentId,
        userId,
        courseId,
        status: 'active',
        progress: 0,
        enrolledAt: new Date(),
        createdAt: new Date(),
        updatedAt: new Date(),
      };
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'ENROLLMENT_ENROLL_FAILED',
        devMessage: `Failed to enroll user ${userId} in course ${courseId}`,
        userMessage: (error as { message?: string })?.message || 'Could not enroll in this course.',
        cause: error,
        level: 'error',
      });
    }
  }

  /**
   * Create an enrollment through the server: for yourself (free courses only)
   * or, as course staff, for one of your students.
   */
  async createEnrollment(enrollmentData: CreateEnrollmentData): Promise<GetEnrollmentRecord> {
    try {
      const self = !auth.currentUser?.uid || auth.currentUser.uid === enrollmentData.userId;
      const { enrollmentId } = self
        ? await certificationApi.enrollInFreeCourse(enrollmentData.courseId)
        : await certificationApi.instructorEnrollStudent(enrollmentData.courseId, enrollmentData.userId);

      return {
        id: enrollmentId,
        userId: enrollmentData.userId,
        courseId: enrollmentData.courseId,
        status: 'ACTIVE',
        progress: 0,
        enrolledAt: new Date(),
        createdAt: new Date(),
        updatedAt: new Date(),
      };
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'ENROLLMENT_CREATE_FAILED',
        devMessage: `Failed to create enrollment for user ${enrollmentData.userId}`,
        userMessage: (error as { message?: string })?.message || 'Could not create the enrollment.',
        cause: error,
        level: 'error',
      });
    }
  }

  async deleteEnrollment(enrollmentId: string): Promise<void> {
    try {
      const docRef = doc(db, this.enrollmentsCol, enrollmentId);
      await deleteDoc(docRef);
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'ENROLLMENT_DELETE_FAILED',
        devMessage: `Failed to delete enrollment ${enrollmentId}`,
        cause: error,
        level: 'error',
      });
    }
  }

  /**
   * Remove a student (course staff). Enrolments are server-only; a browser
   * delete was always refused. The server allows comped and free enrolments
   * and refuses paid ones (access to a paid course ends through a refund).
   */
  async unenrollFromCourse(enrollmentId: string): Promise<void> {
    try {
      await httpsCallable(functions, 'instructorUnenrollStudent')({ enrollmentId });
    } catch (error) {
      if (error instanceof DRXError) throw error;
      const message = (error as { message?: string })?.message;
      throw new DRXError({
        code: 'ENROLLMENT_UNENROLL_FAILED',
        devMessage: `Failed to unenroll enrollment ${enrollmentId}`,
        userMessage: message || 'Could not remove the student. Please try again.',
        cause: error,
        level: 'warning',
      });
    }
  }

  async getUserEnrollments(userId: string): Promise<EnrollmentWithCourse[]> {
    try {
      const enrollmentsRef = collection(db, this.enrollmentsCol);

      // Get enrollments
      const enrollmentsQuery = query(
        enrollmentsRef,
        where('userId', '==', userId),
        where('status', 'in', LIVE_ENROLLMENT_STATUSES),
      );

      // Get course purchases
      const purchasesRef = collection(db, this.coursePurchasesCol);
      const purchasesQuery = query(
        purchasesRef,
        where('userId', '==', userId),
        where('status', '==', 'completed'),
      );
      // Both at once: run one after the other, they (and the course reads
      // below) made My Courses take ~7s for a learner with a dozen enrolments.
      const [enrollmentsSnapshot, purchasesSnapshot] = await Promise.all([
        getDocs(enrollmentsQuery),
        getDocs(purchasesQuery),
      ]);

      // Merge enrollments and purchases, deduplicating by courseId
      const enrollmentMap = new Map<string, Record<string, unknown>>();

      const getEnrollmentCourseId = (record: Record<string, unknown>): string | null =>
        resolveCourseDocId(record?.courseId ?? record?.itemId ?? record?.productId) ??
        this.extractCourseIdFromItems(record);

      for (const enrollmentDoc of enrollmentsSnapshot.docs) {
        const enrollment = { id: enrollmentDoc.id, ...enrollmentDoc.data() };
        const cid = getEnrollmentCourseId(enrollment);
        if (!cid) continue;
        enrollmentMap.set(cid, { ...enrollment, courseId: cid });
      }

      for (const purchaseDoc of purchasesSnapshot.docs) {
        const purchase = purchaseDoc.data();
        const cid = getEnrollmentCourseId(purchase);
        if (!cid || enrollmentMap.has(cid)) continue;
        enrollmentMap.set(cid, {
          id: purchaseDoc.id,
          userId: purchase.userId,
          courseId: cid,
          status: 'active',
          enrolledAt: purchase.purchasedAt || purchase.createdAt,
          isPurchased: true,
          purchaseAmount: purchase.amount,
        });
      }

      // Fetch course details, all in parallel (order kept).
      const fetched = await Promise.all(
        [...enrollmentMap.values()].map(async (enrollment): Promise<EnrollmentWithCourse | null> => {
        const cid = getEnrollmentCourseId(enrollment);
        if (!cid) return null;

        try {
          const courseDoc = await getDoc(doc(db, 'certificationCourses', cid));
          if (courseDoc.exists()) {
            const course = courseDoc.data();
            return {
              enrollment: {
                id: enrollment.id as string,
                status: (enrollment.status as string) || 'ACTIVE',
                enrolledAt: enrolledAtToIso(enrollment.enrolledAt),
                progress: (enrollment.progress as number) || 0,
                // refunded / disputed end access; the card says so.
                paymentStatus: (enrollment.paymentStatus as string) || undefined,
              },
              course: {
                id: courseDoc.id,
                title: course.title,
                description: course.description,
                price: course.price,
                imageUrl: course.imageUrl || course.thumbnailUrl,
                instructorId: course.instructorId,
              },
            };
          }
        } catch {
          // Skip courses that can't be fetched
        }
        return null;
        })
      );

      return fetched.filter((r): r is EnrollmentWithCourse => r !== null);
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'ENROLLMENT_GET_USER_ENROLLMENTS_FAILED',
        devMessage: `Failed to get enrollments for user ${userId}`,
        cause: error,
        level: 'error',
      });
    }
  }

  async getUserPurchases(userId: string): Promise<GetEnrollmentRecord[]> {
    try {
      const purchasesRef = collection(db, this.resourcePurchasesCol);
      const q = query(
        purchasesRef,
        where('userId', '==', userId),
        where('status', '==', 'completed'),
        orderBy('purchasedAt', 'desc'),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as unknown as GetEnrollmentRecord));
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'ENROLLMENT_GET_PURCHASES_FAILED',
        devMessage: `Failed to get purchases for user ${userId}`,
        cause: error,
        level: 'error',
      });
    }
  }

  async getPurchaseBySessionId(sessionId: string): Promise<GetEnrollmentRecord | null> {
    try {
      // Scoped to the signed-in buyer. Purchase rows are only readable by
      // their own buyer (or seller), and Firestore refuses any query it
      // can't prove stays inside that -- so a lookup by session id alone
      // must not be relied on. It also stops one user reading another's
      // purchase by guessing a session id.
      const uid = auth.currentUser?.uid;
      if (!uid) return null;
      for (const colName of ['resourcePurchases', 'coursePurchases', 'subscriptionPurchases']) {
        const colRef = collection(db, colName);
        const q = query(colRef, where('userId', '==', uid), where('stripeSessionId', '==', sessionId), limit(1));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const purchaseDoc = snapshot.docs[0];
          return { id: purchaseDoc.id, ...purchaseDoc.data() } as unknown as GetEnrollmentRecord;
        }
      }

      return null;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'ENROLLMENT_GET_PURCHASE_BY_SESSION_FAILED',
        devMessage: `Failed to get purchase for session ${sessionId}`,
        cause: error,
        level: 'error',
      });
    }
  }

  /**
   * Whether this user is enrolled -- for the Enroll button. checkCourseAccess
   * says yes to every FREE course (anyone may read it), so the course page
   * showed "Already Enrolled" on free courses and nobody could enrol; with no
   * enrolment there is no progress and no certificate (2026-10-14).
   */
  async isEnrolled(userId: string, courseId: string): Promise<boolean> {
    const snap = await getDocs(query(
      collection(db, this.enrollmentsCol),
      where('userId', '==', userId),
      where('courseId', '==', courseId),
    ));
    if (snap.docs.some((d) => isCurrentEnrollment(d.data()))) return true;
    // A paid course: a completed purchase counts in the moments before its
    // enrolment is written (and the instructor counts as enrolled).
    const course = await getDoc(doc(db, 'certificationCourses', courseId));
    const price = Number(course.data()?.price ?? 0);
    return price > 0 ? this.checkCourseAccess(userId, courseId) : false;
  }

  async checkCourseAccess(userId: string, courseId: string): Promise<boolean> {
    try {
      const courseDoc = await getDoc(doc(db, 'certificationCourses', courseId));
      if (!courseDoc.exists()) return false;

      const courseData = courseDoc.data();

      // Free course
      if (!courseData?.price || courseData.price === 0) return true;

      // User is instructor
      if (courseData?.instructorId === userId) return true;

      // Check enrollments
      const enrollmentsRef = collection(db, this.enrollmentsCol);
      const enrollmentQuery = query(
        enrollmentsRef,
        where('userId', '==', userId),
        where('courseId', '==', courseId),
        where('status', 'in', ['active', 'ACTIVE', 'completed', 'COMPLETED']),
      );
      const enrollmentSnapshot = await getDocs(enrollmentQuery);
      // Same rule as the server (isEnrollmentEntitled): a paid course needs a
      // paid or staff-granted enrolment. A refund or chargeback sets
      // paymentStatus to refunded/disputed and must end access; status alone
      // stayed COMPLETED and kept the course open after the money went back.
      if (enrollmentSnapshot.docs.some((d) => d.data().paymentStatus === 'paid' || !!d.data().grantedBy)) return true;

      // Check course purchases
      const purchasesRef = collection(db, this.coursePurchasesCol);
      const purchaseQuery = query(
        purchasesRef,
        where('userId', '==', userId),
        where('courseId', '==', courseId),
        where('status', '==', 'completed'),
      );
      const purchaseSnapshot = await getDocs(purchaseQuery);
      // A purchase still covers the moments before its enrolment is written,
      // but not once it has been refunded or charged back.
      return purchaseSnapshot.docs.some((d) => !['refund', 'dispute'].includes(String(d.data().reversalStatus ?? '')));
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'ENROLLMENT_CHECK_ACCESS_FAILED',
        devMessage: `Failed to check course access for user ${userId}, course ${courseId}`,
        cause: error,
        level: 'error',
      });
    }
  }

  async getCourseQuizzes(courseId: string): Promise<CourseQuizRef[]> {
    try {
      // A quiz belongs to the course directly, or through its module or
      // lesson: lesson quizzes were written with only { lessonId }, so a
      // query by courseId alone missed them and Quiz Management said "No
      // Quizzes Created" for a course whose learners were taking one.
      const quizzesRef = collection(db, 'quizzes');
      const moduleIds = (await getDocs(query(collection(db, 'modules'), where('courseId', '==', courseId)))).docs.map((d) => d.id);
      const lessonIds = (
        await Promise.all(moduleIds.map((m) => getDocs(query(collection(db, 'lessons'), where('moduleId', '==', m)))))
      ).flatMap((snap) => snap.docs.map((d) => d.id));
      const snaps = await Promise.all([
        getDocs(query(quizzesRef, where('courseId', '==', courseId))),
        ...moduleIds.map((m) => getDocs(query(quizzesRef, where('moduleId', '==', m)))),
        ...lessonIds.map((l) => getDocs(query(quizzesRef, where('lessonId', '==', l)))),
      ]);
      const byId = new Map<string, CourseQuizRef>();
      for (const snap of snaps) {
        for (const d of snap.docs) byId.set(d.id, { id: d.id, ...d.data(), courseId } as unknown as CourseQuizRef);
      }
      return [...byId.values()];
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'ENROLLMENT_GET_QUIZZES_FAILED',
        devMessage: `Failed to get quizzes for course ${courseId}`,
        cause: error,
        level: 'error',
      });
    }
  }

  async getCourseCertificates(courseId: string): Promise<CourseCertificateRef[]> {
    try {
      const certificatesRef = collection(db, 'certificates');
      // The course's own instructor may read only certificates naming them
      // (rules), so a query by course alone was refused for them; staff may
      // read the whole course. Same set either way: a course's certificates
      // all carry its instructorId.
      const uid = auth.currentUser?.uid;
      const course = uid ? await getDoc(doc(db, 'certificationCourses', courseId)) : null;
      const q = uid && course?.exists() && course.data().instructorId === uid
        ? query(certificatesRef, where('courseId', '==', courseId), where('instructorId', '==', uid))
        : query(certificatesRef, where('courseId', '==', courseId));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, courseId, ...d.data() } as unknown as CourseCertificateRef));
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'ENROLLMENT_GET_CERTIFICATES_FAILED',
        devMessage: `Failed to get certificates for course ${courseId}`,
        cause: error,
        level: 'error',
      });
    }
  }

  async getEligibleStudentsForCertificates(courseId: string): Promise<EligibleStudent[]> {
    try {
      // Get all enrollments for this course
      const enrollmentsRef = collection(db, this.enrollmentsCol);
      const enrollmentsQuery = query(
        enrollmentsRef,
        where('courseId', '==', courseId),
        orderBy('enrolledAt', 'desc'),
      );
      const enrollmentsSnapshot = await getDocs(enrollmentsQuery);

      const enrollments = enrollmentsSnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      // Get existing certificates
      const certificates = await this.getCourseCertificates(courseId);
      const certifiedUserIds = certificates.map((cert) => cert.userId).filter(Boolean);

      // Filter to active students without certificates
      const eligibleEnrollments = enrollments.filter((e) => {
        const rec = e as Record<string, unknown>;
        const uid = rec.userId;
        return (
          typeof uid === 'string' &&
          !certifiedUserIds.includes(uid) &&
          // Free enrolments are written 'active', paid ones 'ACTIVE'; only
          // 'ACTIVE' matched, so free-course students never appeared and
          // could not be issued a certificate by hand.
          ['active', 'completed'].includes(String(rec.status ?? '').toLowerCase())
        );
      });

      // Fetch user data
      const eligibleStudents: EligibleStudent[] = [];
      for (const enrollment of eligibleEnrollments) {
        const rec = enrollment as Record<string, unknown>;
        const enrollmentUserId = rec.userId;
        if (typeof enrollmentUserId !== 'string') continue;
        try {
          const userDoc = await getDoc(profileDocRef(enrollmentUserId));
          if (userDoc.exists()) {
            const user = userDoc.data();
            eligibleStudents.push({
              id: userDoc.id,
              name:
                `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
                user.username ||
                user.email,
              email: user.email,
              enrollmentId: rec.id as string,
              progress: (typeof rec.progress === 'number' ? rec.progress : 0) || 0,
            });
          }
        } catch {
          // Skip users that can't be fetched
        }
      }

      return eligibleStudents;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'ENROLLMENT_GET_ELIGIBLE_STUDENTS_FAILED',
        devMessage: `Failed to get eligible students for course ${courseId}`,
        cause: error,
        level: 'error',
      });
    }
  }

  async getCourseStudents(courseId: string): Promise<any[]> {
    try {
      const enrollmentsRef = collection(db, this.enrollmentsCol);
      const enrollmentsQuery = query(
        enrollmentsRef,
        where('courseId', '==', courseId),
        where('status', 'in', LIVE_ENROLLMENT_STATUSES),
      );
      const enrollmentsSnapshot = await getDocs(enrollmentsQuery);

      const students: any[] = [];
      for (const enrollmentDoc of enrollmentsSnapshot.docs) {
        const enrollmentData = enrollmentDoc.data();
        try {
          const userDoc = await getDoc(profileDocRef(enrollmentData.userId));
          if (userDoc.exists()) {
            const user = userDoc.data();
            students.push({
              id: userDoc.id,
              ...user,
              enrollmentId: enrollmentDoc.id,
              enrollmentStatus: enrollmentData.status,
              enrolledAt: enrollmentData.enrolledAt,
            });
          }
        } catch {
          // Skip users that can't be fetched
        }
      }
      return students;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'ENROLLMENT_GET_COURSE_STUDENTS_FAILED',
        devMessage: `Failed to get students for course ${courseId}`,
        cause: error,
        level: 'error',
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private extractCourseIdFromItems(record: Record<string, unknown>): string | null {
    if (!Array.isArray(record?.items)) return null;
    type ItemLike = {
      type?: string;
      productType?: string;
      courseId?: string;
      itemId?: string;
      productId?: string;
      id?: string;
    };
    const items = record.items as ItemLike[];
    const courseItem =
      items.find((item) => item?.type === 'course' || item?.productType === 'course') ||
      items[0];

    return resolveCourseDocId(
      courseItem?.courseId || courseItem?.itemId || courseItem?.productId || courseItem?.id,
    );
  }
}
